import { Component, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material';
import { FcPaginatorComponent } from '@core/components/fc-paginator/fc-paginator.component';
import { PagedResult, User } from '@core/interfaces';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GroupService } from '../../../../services/groups-api.service';

@Component({
  selector: 'linked-users',
  templateUrl: './linked-users.component.html',
  styleUrls: ['../tab-users.component.scss']
})
export class LinkedUsersComponent implements OnInit, OnDestroy {
  @Input() public isViewMode: boolean;
  @Input() public searchInputValue: FormControl;
  @Input() public groupId: string;
  @Input() public reload: Subject<void>;

  public subscriptions = new Subject();
  public page: PagedResult<User> = null;
  public isLoading = true;
  public pageSize = 5;
  public currentPage = 0;
  public isPageChanging = false;

  @ViewChild('paginator', { static: false })
    public paginator: FcPaginatorComponent;

  constructor(
    private groupService: GroupService
  ) {}

  public ngOnInit(): void {
    this.searchInputValue.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.isLoading = true;
      this.reload.next();
    });

    this.reload.pipe(
      takeUntil(this.subscriptions),
      tap(() => this.isLoading = true),
      switchMap(() => this.searchUsers()),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe((userPage) => {
      this.page = userPage;
    });

    this.reload.next();
  }

  public ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  public async unlinkUser(userId: string): Promise<void> {
    if (!this.groupId) {
      return;
    }

    this.isLoading = true;
    await this.groupService.unlinkUser(userId, this.groupId);
    this.isLoading = false;
    this.reload.next();
    return;
  }

  private async searchUsers(): Promise<PagedResult<User>> {
    if (!this.groupId) {
      return null;
    }

    return this.groupService.listUsers({
      limit: this.pageSize,
      offset: this.currentPage * this.pageSize,
      where: {
        name_or_email_contains: this.searchInputValue.value || '',
        group_id: this.groupId
      }
    });
  }

  public async onPageChange(event: PageEvent): Promise<void> {
    this.currentPage = event.pageIndex;
    this.isPageChanging = true;
    this.page = await this.searchUsers();
    this.isPageChanging = false;
    this.isLoading = false;
  }
}
